import { PencilIcon, TrashIcon } from "@heroicons/react/outline";
import { useState } from "react";

const MainInfoTab = ({ booking }) => {
  const formatLocation = (address) => {
    if (!address) return "-";

    const parts = [address.city, address.country];

    const filtered = parts.filter((p) => p && p.trim() !== "");

    return filtered.length ? filtered.join(", ") : "-";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-7 gap-6 text-sm p-4">
      {/* LEFT – Booking Info */}
      <div className="lg:col-span-2 bg-slate-900/80 border border-white/10 rounded-lg p-4">
        <h3 className="text-white font-semibold mb-5">Booking Details</h3>

        <Info label="Promoter Name" value={booking.promoter?.name} />
        <Info label="Event Date" value={booking.date} />
        <Info label="Town / Country" value={formatLocation(booking.address)} />
        <Info label="Set Time" value={booking.set_time} />
        <Info label="Artist" value={booking.artist_name} />
        <Info label="Payment Status" value={booking.payment_status} />
        <Info label="Booking Status" value={booking.status} />

        {/* Event Flyer */}
        <div className="mt-6">
          <p className="text-slate-400 mb-2">Event Flyer</p>

          <div className="bg-slate-800 border border-white/10 rounded-lg h-40 flex items-center justify-center">
            {booking.event_flyer ? (
              <img
                src={booking.flyer}
                alt="Event Flyer"
                className="h-full w-full object-cover rounded-lg"
              />
            ) : (
              <span className="text-slate-500">No Flyer</span>
            )}
          </div>
        </div>
      </div>

      {/* MIDDLE – Financial + Links */}
      <div className="lg:col-span-3 space-y-4">
        {/* Financial Information */}
        <div className="bg-slate-900/80 border border-white/10 rounded-lg p-4">
          <h3 className="text-white font-semibold mb-4">
            Financial Information
          </h3>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="border-b border-white/10 text-slate-400">
                <tr>
                  <th className="text-left py-2">Invoice / Proforma</th>
                  <th className="text-left py-2">Amount</th>
                  <th className="text-left py-2">Payment Status</th>
                  <th className="text-right py-2">Actions</th>
                </tr>
              </thead>

              <tbody>
                {(booking.payment_instalments || []).map((invoice) => (
                  <tr
                    key={invoice.id}
                    className="border-b border-white/5 hover:bg-slate-800/40 transition"
                  >
                    <td className="py-2 text-white">{invoice.document_type}</td>

                    <td className="py-2 text-slate-400">
                      {invoice.amount} {invoice.currency}
                    </td>

                    <td className="py-2 text-slate-400">
                      {invoice.payment_status}
                    </td>

                    <td className="py-2 text-right space-x-3">
                      <button className="text-slate-400 hover:text-white">
                        <PencilIcon className="w-4 h-4 inline" />
                      </button>
                      <button className="text-red-400 hover:text-red-500">
                        <TrashIcon className="w-4 h-4 inline" />
                      </button>
                    </td>
                  </tr>
                ))}

                {!booking.invoices?.length && (
                  <tr>
                    <td colSpan={4} className="py-6 text-center text-slate-400">
                      No invoices available
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Important Links */}
        <div className="bg-slate-900/80 border border-white/10 rounded-lg p-4">
          <h3 className="text-white font-semibold mb-4">Important Links</h3>

          <div className="space-y-2">
            {booking.links?.length ? (
              booking.links.map((link, index) => (
                <a
                  key={index}
                  href={link.url}
                  target="_blank"
                  rel="noreferrer"
                  className="block text-pink-500 hover:underline"
                >
                  {link.label}
                </a>
              ))
            ) : (
              <p className="text-slate-400 text-sm">No links available</p>
            )}
          </div>
        </div>
      </div>

      {/* RIGHT – Logs */}
      <div className="lg:col-span-2 bg-slate-900/80 border border-white/10 rounded-lg p-4 flex flex-col">
        <h3 className="text-white font-semibold mb-3">Logs</h3>

        <div className="flex-1 overflow-y-auto pr-2 space-y-4 max-h-96">
          {booking.activity_logs?.length ? (
            booking.activity_logs.map((log) => (
              <div
                key={log.id}
                className="border-b border-white/10 pb-3 last:border-none"
              >
                <div className="flex items-center justify-between">
                  <span className="text-sm text-white font-medium">
                    {log.action}
                  </span>

                  <span className="text-xs text-slate-400">
                    {formatDate(log.date)}
                  </span>
                </div>

                <p className="text-sm text-slate-400 mt-1">{log.description}</p>
              </div>
            ))
          ) : (
            <p className="text-slate-400 text-sm">No logs available</p>
          )}
        </div>
      </div>
    </div>
  );
};

const Info = ({ label, value }) => (
  <div className="mb-2">
    <p className="text-slate-400">{label}</p>
    <p className="text-white">{value || "-"}</p>
  </div>
);

export default MainInfoTab;
